import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_LOCALE, useLocale, type Locale } from "@/hooks/use-locale";
import { getPreloadedMenuNodes, localeKeyForMenu, setPreloadedMenuNodes } from "@/lib/menu-preload";
import { getMenu, toSpaHref, type MenuNode } from "@/lib/wp";
import { cn } from "@/lib/utils";

type FooterNavLink = {
  id: number;
  title: string;
  href: string;
  isExternal: boolean;
};

type FooterNavGroup = {
  id: number;
  title: string;
  links: FooterNavLink[];
};

function mapFooterMenu(nodes: MenuNode[] | undefined, lang: string): FooterNavGroup[] {
  if (!nodes || nodes.length === 0) return [];

  return nodes.map((node) => {
    const children = node.children ?? [];
    const links: FooterNavLink[] = children.length
      ? children.map((child) => {
          const href = toSpaHref(child.url, lang);
          return {
            id: child.id,
            title: child.title,
            href,
            isExternal: !href.startsWith("/"),
          };
        })
      : (() => {
          const href = toSpaHref(node.url, lang);
          if (!href) return [] as FooterNavLink[];
          return [
            {
              id: node.id,
              title: node.title,
              href,
              isExternal: !href.startsWith("/"),
            },
          ];
        })();

    return {
      id: node.id,
      title: node.title,
      links,
    };
  });
}

function normalizeFooterTitle(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
}

const HIGHLIGHT_SPECS = {
  en: [
    { key: "blog", labels: ["blog"] },
    { key: "case-studies", labels: ["case studies"] },
    { key: "about", labels: ["about us", "about"] },
    { key: "news", labels: ["news & announcements", "news"] },
  ],
  fr: [
    { key: "blog", labels: ["blog"] },
    { key: "cases", labels: ["etudes de cas", "études de cas"] },
    { key: "about", labels: ["a propos", "à propos"] },
    { key: "news", labels: ["actualites", "actualités"] },
  ],
} as const;

function extractHighlightLinks(groups: FooterNavGroup[], locale: string) {
  const specList = locale === "fr" ? HIGHLIGHT_SPECS.fr : HIGHLIGHT_SPECS.en;
  const normalizedSpecs = specList.map((spec, index) => ({
    order: index,
    key: spec.key,
    labels: spec.labels.map(normalizeFooterTitle),
  }));

  const highlightsWithMeta: Array<{ order: number; link: FooterNavLink }> = [];
  const filteredGroups: FooterNavGroup[] = [];

  groups.forEach((group) => {
    const groupNormalized = normalizeFooterTitle(group.title);
    const isCompanyGroup = groupNormalized === "company" || groupNormalized === "entreprise";
    const remainingLinks: FooterNavLink[] = [];

    group.links.forEach((link) => {
      const normalized = normalizeFooterTitle(link.title);
      const match = normalizedSpecs.find((spec) => spec.labels.includes(normalized));
      if (match) {
        highlightsWithMeta.push({ order: match.order, link });
      } else {
        remainingLinks.push(link);
      }
    });

    if (!isCompanyGroup && remainingLinks.length > 0) {
      filteredGroups.push({ ...group, links: remainingLinks });
    }
  });

  highlightsWithMeta.sort((a, b) => a.order - b.order);

  return {
    filtered: filteredGroups,
    highlights: highlightsWithMeta.map((item) => item.link),
  };
}

function buildFooterContent(nodes: MenuNode[] | null, locale: string) {
  if (!nodes || nodes.length === 0) {
    return { groups: [] as FooterNavGroup[], highlights: [] as FooterNavLink[] };
  }
  const mapped = mapFooterMenu(nodes, locale);
  const { filtered, highlights } = extractHighlightLinks(mapped, locale);
  return { groups: filtered, highlights };
}

export default function Footer() {
  const locale = useLocale();
  const localeKey = useMemo(() => localeKeyForMenu(locale), [locale]);
  const preloadedNodes = useMemo(() => getPreloadedMenuNodes(localeKey, "footer"), [localeKey]);
  const initialContent = useMemo(() => buildFooterContent(preloadedNodes, locale), [preloadedNodes, locale]);
  const [groups, setGroups] = useState<FooterNavGroup[]>(initialContent.groups);
  const [highlightLinks, setHighlightLinks] = useState<FooterNavLink[]>(initialContent.highlights);
  const [error, setError] = useState<string | null>(null);
  const contactHref = useMemo(() => toSpaHref("/company/support/contact-form", locale), [locale]);
  const supportTermsHref = useMemo(() => toSpaHref("/legal/support-terms", DEFAULT_LOCALE), []);
  const privacyHref = useMemo(() => toSpaHref("/legal/privacy-policy", DEFAULT_LOCALE), []);
  const saasHref = useMemo(() => toSpaHref("/legal/saas-agreements", DEFAULT_LOCALE), []);
  useEffect(() => {
    if (preloadedNodes) {
      const { groups: nextGroups, highlights: nextHighlights } = buildFooterContent(preloadedNodes, locale);
      setGroups(nextGroups);
      setHighlightLinks(nextHighlights);
      setError(null);
    } else {
      setGroups([]);
      setHighlightLinks([]);
    }
  }, [preloadedNodes, locale]);

  useEffect(() => {
    if (preloadedNodes) {
      return;
    }

    let active = true;
    setError(null);

    (async () => {
      try {
        let items = await getMenu("footer", locale);
        let usedLocale: Locale = locale;
        if ((!items || items.length === 0) && locale !== DEFAULT_LOCALE) {
          items = await getMenu("footer", DEFAULT_LOCALE);
          usedLocale = DEFAULT_LOCALE;
        }
        if (!active) return;

        setPreloadedMenuNodes(localeKey, "footer", items);
        if (usedLocale !== locale) {
          const fallbackKey = localeKeyForMenu(usedLocale);
          setPreloadedMenuNodes(fallbackKey, "footer", items);
        }

        const { groups: nextGroups, highlights: nextHighlights } = buildFooterContent(items, usedLocale);
        setGroups(nextGroups);
        setHighlightLinks(nextHighlights);
      } catch (err) {
        if (!active) return;
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setGroups([]);
        setHighlightLinks([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [locale, localeKey, preloadedNodes]);

  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <footer className="bg-onplexus-dark text-white">
      <div className="h-6 bg-gradient-to-b from-transparent to-onplexus-darker" />
      <div className="px-4 py-12">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-12">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {groups.map((group) => (
              <div key={group.id} className="space-y-3">
                <h3 className="text-lg font-bold text-white">{group.title}</h3>
                <ul className="space-y-2 text-sm">
                  {group.links.map((link) => (
                    <li key={link.id}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {highlightLinks.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {highlightLinks.map((link) => (
                <FooterLink key={`highlight-${link.id}`} link={link} />
              ))}
            </div>
          )}

          {error && (
            <p className="text-xs text-white/60">{error}</p>
          )}

          <div className="h-px bg-white/10" />

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3 max-w-md">
              <FooterLogo className="h-9" />
              <p className="text-sm text-white/75">
                OnPlexus provides platform access to an international payment network. All regulated payment
                services are powered by Payment Partners. Client funds remain within the regulated environment
                throughout the payment lifecycle; OnPlexus does not take custody of client funds.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2 text-sm">
                <h4 className="text-base font-semibold text-white">Contact Us</h4>
                <p className="text-white/80">
                  Email <a href="mailto:info@onplexus.com" className="font-semibold text-onplexus-gold">info@onplexus.com</a>
                  {" "}or complete our {" "}
                  <Link to={contactHref} className="font-semibold text-onplexus-gold">
                    contact form
                  </Link>
                  .
                </p>
              </div>
              <div className="space-y-2 text-sm">
                <h4 className="text-base font-semibold text-white">London, UK</h4>
                <p className="text-white/80">
                  231 Vauxhall Bridge Road,<br />
                  London, SW1V 1AD,<br />
                  United Kingdom
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-white/10" />

          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between text-sm text-white/75">
            <p>©{year} CurrencyTransfer Ltd. All rights reserved.</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link className="text-onplexus-gold hover:text-onplexus-gold/80" to={supportTermsHref}>
                Support Terms
              </Link>
              <Link className="text-onplexus-gold hover:text-onplexus-gold/80" to={privacyHref}>
                Privacy Policy
              </Link>
              <Link className="text-onplexus-gold hover:text-onplexus-gold/80" to={saasHref}>
                SaaS Agreement
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ link }: { link: FooterNavLink }) {
  const className = cn("text-onplexus-gold font-semibold hover:text-onplexus-gold/80 transition-colors");
  if (link.isExternal) {
    return (
      <a href={link.href} target="_blank" rel="noreferrer" className={className}>
        {link.title}
      </a>
    );
  }
  return (
    <Link to={link.href} className={className}>
      {link.title}
    </Link>
  );
}

function FooterLogo({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 72" className={cn("w-auto", className)}>
      <defs>
        <linearGradient id="footer-gradient-1" x1="21.42" y1="-1.07" x2="37.15" y2="33.25" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ebc738" />
          <stop offset=".28" stopColor="#e9c335" />
          <stop offset=".52" stopColor="#e4b92d" />
          <stop offset=".73" stopColor="#dca81f" />
          <stop offset=".93" stopColor="#d1910d" />
        </linearGradient>
        <linearGradient id="footer-gradient-2" x1="39.01" y1="66.8" x2="27.09" y2="33.6" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ebc738" />
          <stop offset=".27" stopColor="#e9c335" />
          <stop offset=".49" stopColor="#e4b92d" />
          <stop offset=".69" stopColor="#dca81f" />
          <stop offset=".88" stopColor="#d1910d" />
        </linearGradient>
      </defs>
      <path d="M63.09 1.46c0-.38-.3-.68-.68-.68-.12 0-.23.04-.32.09-7.29 4.16-14.74 4.96-22.28 3.68h.03c-2.01-.37-4.09-.58-6.21-.58-8.15 0-15.61 2.96-21.37 7.85a33.24 33.24 0 00-3.58 3.54C3.88 20.9 1 28.1 1 35.98s2.79 14.86 7.43 20.36l11.23-11.23a15.786 15.786 0 01-2.89-9.13c0-8.76 7.1-15.85 15.85-15.85 3.4 0 6.55 1.07 9.13 2.89l11.23-11.23.88-.88 9.1-9.02-.02-.02c.09-.11.15-.25.15-.4z" fill="url(#footer-gradient-1)" />
      <path d="M2.14 70.54c0 .38.3.68.68.68.12 0 .23-.04.32-.09 7.29-4.16 14.74-4.96 22.28-3.68h-.03c2.01.37 4.09.58 6.21.58 8.15 0 15.61-2.96 21.37-7.85a33.24 33.24 0 003.58-3.54c4.77-5.54 7.65-12.74 7.65-20.62s-2.79-14.86-7.43-20.36L45.54 26.89c1.82 2.58 2.89 5.73 2.89 9.13 0 8.76-7.1 15.85-15.85 15.85-3.4 0-6.55-1.07-9.13-2.89L12.22 60.21l-.88.88-9.1 9.03.02.02c-.09.11-.15.25-.15.4z" fill="url(#footer-gradient-2)" />
      <path fill="#fff" d="M190.97 12.07h-17.35v48.08h8.92V46.78h8.43c3.02 0 5.85-.77 8.33-2.13 5.38-2.95 9.03-8.66 9.03-15.23 0-9.58-7.77-17.35-17.35-17.35zm0 25.8h-8.43V21h8.43c4.66 0 8.44 3.78 8.44 8.44s-3.78 8.44-8.44 8.44zM141.18 39.94v20.78h-8.92V18.51l24.34 22.86v-20.8h8.92v42.2l-24.34-22.83zM298.3 61.04l-8.21-12.81-8.05 12.81h-8.94l12.2-20.68-11.73-20.01h9l7.65 12.07 7.48-12.07h9.08l-11.72 19.9 12.19 20.79h-8.95zM213.89 61.04V20.19h8.74V52.3h15.99v8.74h-24.73zM244.18 61.04V20.35h26.14v8.75h-17.4v7.22h8.7v8.75h-8.7v7.23h17.4v8.74h-26.14zM101.66 10.96c-13.83 0-25.04 11.21-25.04 25.04s11.21 25.04 25.04 25.04S126.7 49.83 126.7 36s-11.21-25.04-25.04-25.04zm0 40.83c-8.72 0-15.79-7.07-15.79-15.79s7.07-15.79 15.79-15.79 15.79 7.07 15.79 15.79-7.07 15.79-15.79 15.79zM335.79 20.35v24.57c0 4.59-3.71 8.31-8.3 8.31s-8.31-3.72-8.31-8.31v-24.6h-8.78v24.6c0 9.44 7.66 17.08 17.08 17.08s17.08-7.71 17.08-17.08V20.35h-8.78zM365.65 37.46c-8.12-2.23-8.8-3.3-8.8-6.02.03-.05-.21-3.62 5.46-3.62 3.5 0 7 1.27 10.39 3.79l4.33-6.27c-4.84-3.5-9.48-5.13-14.6-5.13-13.93 0-13.39 12.25-13.3 12.03.25 6.19 4.08 9.84 12.81 12.2 6.68 1.8 8.24 2.57 8.15 6-.03.05.37 3.91-6.13 3.91-4.22 0-8.13-1.53-11.96-4.68l-4.89 5.86c4.67 4.15 10.59 6.44 16.67 6.44 14.95 0 14.05-12.43 14.03-12.38.13-5.87-3.85-9.83-12.16-12.12" />
    </svg>
  );
}
